import { Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import TableHead from '@mui/material/TableHead';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../../../constants/language';
import { ITableSummary, OrderSummaryData } from '../../../../interfaces/index.types';
import { formatPriceItem } from '../../../../utils';
import FoodTruckFilter from '../FoodTruckFilter/FoodTruckFilter';

interface DataColumn {
  key: string;
  label: string;
  color: string;
  backgroundColor: string;
}

interface RowDefinition {
  field: keyof OrderSummaryData;
  label: string;
  format?: (value: number) => string | number;
  suffix?: string;
}

const COLUMN_CONFIG: Record<string, { label: string; color: string; backgroundColor: string }> = {
  regular_order: { label: 'REGULAR_ORDERS', color: '#D3F8DF', backgroundColor: '#EDFCF2' },
  regular_pre_order: { label: 'REGULAR_PRE_ORDERS', color: '#E0F2FE', backgroundColor: '#F0F9FF' },
  pre_order: { label: 'PRE_ORDERS', color: '#FFE6D5', backgroundColor: '#FFF4ED' },
  special_order: { label: 'SPECIAL_ORDERS', color: '#EBE9FE', backgroundColor: '#F4F3FF' },
  special_pre_order: { label: 'SPECIAL_PRE_ORDERS', color: '#FFE6D5', backgroundColor: '#FFF4ED' },
};

const ROW_DEFINITIONS: RowDefinition[] = [
  { field: 'totalOrders', label: 'TOTAL_ORDER' },
  { field: 'totalDeliveredOrder', label: 'TOTAL_DELIVERED_ORDER' },
  { field: 'totalCancelledOrder', label: 'TOTAL_CANCELLED_ORDER' },
  { field: 'totalDeliveryFee', label: 'DELIVERY_FEE', format: formatPriceItem, suffix: '¥' },
  { field: 'totalFoodRevenue', label: 'FOOD_REVENUE', format: formatPriceItem, suffix: '¥' },
  { field: 'totalOrderAmount', label: 'TOTAL', format: formatPriceItem, suffix: '¥' },
  { field: 'totalDamagedOrder', label: 'TOTAL_DAMAGED_ORDER' },
  { field: 'totalDamagedFee', label: 'TOTAL_DAMAGED_FEE', format: formatPriceItem, suffix: '¥' },
];

function TableSummary({ style, tableCell, showForSuperAdmin, data, selectedFoodTrucks, onFilterChange, routeType }: ITableSummary) {
  const { t } = useTranslation();

  const isPreOrders = routeType === 'pre_orders';
  const isOrderHistory = routeType === 'order_history';

  const dataColumns: DataColumn[] = useMemo(() => {
    const availableKeys = Object.keys(data);
    const canonicalOrderHistory = ['regular_order', 'regular_pre_order', 'special_order', 'special_pre_order'];
    const canonicalPreOrders = ['regular_pre_order', 'special_pre_order'];
    const normalize = (k: string) => k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const findActualKey = (canonicalKey: string) => {
      const target = normalize(canonicalKey);
      return availableKeys.find((k) => normalize(k) === target) || availableKeys.find((k) => normalize(k).includes(target));
    };

    const keysToUse = isPreOrders
      ? (canonicalPreOrders.map(findActualKey).filter(Boolean) as string[])
      : isOrderHistory
        ? (canonicalOrderHistory.map(findActualKey).filter(Boolean) as string[])
        : availableKeys;

    return keysToUse
      .filter((key) => Boolean(data[key as keyof typeof data]))
      .map((key) => ({
        key,
        label: COLUMN_CONFIG[key]?.label || key.toUpperCase(),
        color: COLUMN_CONFIG[key]?.color || '#E0E0E0',
        backgroundColor: COLUMN_CONFIG[key]?.backgroundColor || '#F5F5F5',
      }));
  }, [data, routeType, isPreOrders, isOrderHistory]);

  const calculateTotal = (field: keyof OrderSummaryData): number =>
    dataColumns.reduce((sum, col) => {
      const colData = data[col.key as keyof typeof data];
      return sum + (colData?.[field] || 0);
    }, 0);

  const calculateAdjustedTotal = (field: keyof OrderSummaryData): number => calculateTotal(field);

  const getDisplayValue = (field: keyof OrderSummaryData, colData: OrderSummaryData | null | undefined): number => colData?.[field] || 0;

  const getSpecialSuffix = (field: keyof OrderSummaryData): string => {
    if (field === 'totalDeliveryFee') return ' (100%)';
    if (field === 'totalFoodRevenue') return ' (10%)';
    return '';
  };

  const superAdminTotals = useMemo(() => {
    const totals: Record<string, string | number> = {};
    ROW_DEFINITIONS.forEach((row) => {
      if (!row.format) {
        totals[row.field] = calculateAdjustedTotal(row.field);
      } else if (row.field === 'totalFoodRevenue') {
        totals[row.field] = `${row.suffix} ${row.format(Math.ceil(calculateAdjustedTotal(row.field) * 0.1))} (10%)`;
      } else if (row.field === 'totalOrderAmount') {
        const total = calculateAdjustedTotal('totalDeliveryFee') + Math.ceil(calculateAdjustedTotal('totalFoodRevenue') * 0.1);
        totals[row.field] = `${row.suffix} ${row.format(total)}`;
      } else {
        totals[row.field] = `${row.suffix} ${row.format(calculateAdjustedTotal(row.field))}${getSpecialSuffix(row.field)}`;
      }
    });
    return totals;
  }, [dataColumns, data]);

  return (
    <Table stickyHeader aria-label="sticky table" sx={{ borderCollapse: 'collapse' }}>
      <TableHead className={style}>
        <TableRow>
          <TableCell
            style={{
              minWidth: 100,
              border: '1px solid #e0e0e0',
              padding: '0px',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }}
          >
            {showForSuperAdmin && (
              <FoodTruckFilter
                selectedFoodTrucks={selectedFoodTrucks}
                onFilterChange={onFilterChange}
                style={{ width: '100%', justifyContent: 'flex-start', border: 'none', padding: '4px 8px' }}
              />
            )}
          </TableCell>
          {dataColumns.map((column) => (
            <TableCell
              key={column.key}
              style={{
                minWidth: 150,
                border: '1px solid #e0e0e0',
                padding: '2px 8px',
                backgroundColor: column.color,
              }}
            >
              {t(column.label)}
            </TableCell>
          ))}
          {showForSuperAdmin && (
            <TableCell
              style={{
                minWidth: 200,
                border: '1px solid #e0e0e0',
                padding: '2px 8px',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }}
            >
              {t('ASTROLAB')}
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {ROW_DEFINITIONS.map((row, rowIndex) => (
          <TableRow key={rowIndex} hover>
            <TableCell className={tableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {t(LANG_TEXT[row.label as keyof typeof LANG_TEXT] || row.label)}
              </Typography>
            </TableCell>
            {dataColumns.map((column, colIndex) => {
              const colData = data[column.key as keyof typeof data];
              const value = getDisplayValue(row.field, colData);
              const formattedValue = row.format ? `${row.suffix} ${row.format(value)}` : value;

              return (
                <TableCell key={colIndex} className={tableCell} sx={{ backgroundColor: column.backgroundColor }}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {formattedValue}
                  </Typography>
                </TableCell>
              );
            })}
            {showForSuperAdmin && (
              <TableCell className={tableCell}>
                <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                  {superAdminTotals[row.field]}
                </Typography>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default TableSummary;
